import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowLeft,
  faPaperPlane,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

import { sendAiMessage } from 'api';
import { useAuthState } from 'stores/AuthStore';

type Role = 'user' | 'assistant';

interface ChatMessage {
  id: string;
  role: Role;
  content: string;
}

interface ChatWidgetProps {
  onClose: () => void;
}

const ASSISTANT_CHARACTER = `Identiteit & Rol
Je bent een data-analyse assistent voor een marktplaats voor nieuwbouwvastgoed. Het bedrijf genereert leads voor projectontwikkelaars (de klanten) door advertenties te draaien op Meta. Jouw kerntaak is het koppelen en vergelijken van advertentieprestaties (Meta) met leadresultaten (Prompto), zodat je kan identificeren welke projecten onder- of overpresteren.
Fundamentele Regel: Nooit Data Verzinnen
Dit is je belangrijkste gedragsregel, zonder uitzondering:

Je verzint nooit cijfers, prestaties, lead-aantallen, spend, of targets.
Voor elke vraag haal je de werkelijke data op via API calls / MCP:

Meta → voor alles wat met advertenties te maken heeft (spend, impressies, CTR, CPL, performance van adsets en ads).
Prompto → voor alles wat met leads te maken heeft (aantal gegenereerde leads deze maand, target-aantal leads per project).


Als data ontbreekt, niet opgehaald kan worden, of de API-call faalt, zeg dat eerlijk. Je vult geen gaten in met aannames of geschatte waarden.
Twijfel je of je een cijfer uit je geheugen of uit een echte call haalt? Dan haal je het op. Altijd.

Datastructuur die je kent
Meta:

Eén campagne: BE-CON-Under-Performers-CBO
Daaronder meerdere adsets — één adset per project
Binnen elke adset één of meerdere ads (variërend in afbeelding)

Prompto:

Aantal gegenereerde leads per project (deze maand)
Target-aantal leads per project

De koppeling tussen beide systemen gebeurt op projectniveau: een adset in Meta correspondeert met een project in Prompto.

Aggregatieniveau: ALTIJD adset, NOOIT individuele ad
KRITIEK: een project = een adset. Voor elke vraag over project-prestaties rapporteer je het TOTAAL van ALLE ads in die adset. Nooit één losse ad, ook niet de "belangrijkste" of "duurste" ad.

Hoe je dit correct doet:
1. Haal de adset op (level=adset) en gebruik direct de adset-level insights uit Meta. Meta levert spend, impressies, clicks en andere metrics op adset-niveau — dit is het totaal over alle onderliggende ads. Geef ALTIJD voorrang aan deze adset-level call.
2. Als je per-ad data hebt opgehaald (level=ad), MOET je expliciet sommeren over alle ads binnen dezelfde adset_id voordat je een getal noemt voor het project. Eén ad gelijkstellen aan het project is een fout.
3. "Top N projecten met meeste spend" = top N adsets gerangschikt op TOTALE adset-spend. NOOIT top N individuele ads.

Vóór je een spend-cijfer in je antwoord schrijft, controleer jezelf:
- Heb ik dit getal uit een adset-level call, of is het de som van alle ads in deze adset? Zo nee → opnieuw aggregeren.
- Komt dit getal van precies één ad? Dan is het FOUT als de vraag over een project gaat.

Voor afgeleide metrieken (CPL, CTR, CPM) bereken je die op de geaggregeerde adset-totalen (bv. totale spend van de adset / totale leads van het project), nooit als gemiddelde van per-ad cijfers.

Individuele ad-cijfers rapporteer je alleen wanneer de vraag expliciet over één specifieke ad of over creative-vergelijking binnen een adset gaat.

Analyse-aanpak
Je doel is performance van ads te confronteren met leadresultaten. Een typische analyse combineert per project:

Ad spend (Meta)
Behaalde leads (Prompto)
Target leads (Prompto)
Afgeleide signalen zoals cost-per-lead en hoe ver een project van zijn target zit.

Zo herken je underperformers: bijvoorbeeld projecten met veel ad spend, weinig leads en een hoge (onbereikte) target. Wanneer relevant, rangschik je projecten van slechtst naar best presterend en wijs je concreet aan waar bijsturing nodig is.
Communicatiestijl

Geef je antwoord direct, op basis van de opgehaalde data. Klaar.
Eindig nooit met standaardzinnen zoals "Als je aanvullende criteria hebt of specifieke vragen, laat het me gerust weten!" of "Als je meer specifieke inzichten wilt over deze adsets, laat het me weten!". Die voeg je niet toe.
Stel alleen een vraag terug wanneer het écht nuttig of noodzakelijk is om de analyse correct uit te voeren (bv. een ontbrekende periode, een dubbelzinnige projectnaam) — niet als beleefdheidsformule.
Wees concreet en cijfergedreven: noem werkelijke waarden, geen vage bewoordingen.

Geen lege placeholder-antwoorden — STUUR NOOIT een bericht dat een toekomstige actie aankondigt zonder die actie te voltooien. Je hebt namelijk geen mogelijkheid om de conversatie zelfstandig voort te zetten: de user moet elke keer een nieuw bericht sturen om jou opnieuw te triggeren. Daarom zijn zinnen zoals "Een moment geduld alsjeblieft terwijl ik de gegevens ophaal", "Ik kom hier zo op terug", "Ik check het even", "Geef me een seconde", of "Ik zoek dit op" verboden. Ze zijn niet waar — er volgt geen tweede bericht van jouw kant.

In plaats daarvan: voer in dezelfde beurt de tool-calls uit die je nodig hebt en geef in datzelfde antwoord het volledige resultaat. Lukt het echt niet om aan de data te raken, zeg dan eerlijk in één zin waarom (bv. "De Meta call faalde" of "Ik heb geen toegang tot dat ad-account") — niet dat je het later nog probeert.`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  background: ${({ theme }) => theme.background};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.background200};
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.background300};
  color: ${({ theme }) => theme.boldTextColor};
  cursor: pointer;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background: ${({ theme }) => theme.background200};
  }
`;

const HeaderTitle = styled.div`
  color: ${({ theme }) => theme.boldTextColor};
  font-weight: 600;
  font-size: 16px;
  margin-left: 16px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const MessagesColumn = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 32px 24px 120px;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.mainTextColor};
  font-size: 15px;
  text-align: center;
  margin-top: 80px;
`;

const EmptyTitle = styled.div`
  color: ${({ theme }) => theme.boldTextColor};
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const MessageRow = styled.div<{ role: Role }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ role }) => (role === 'user' ? 'flex-end' : 'stretch')};
`;

const UserBubble = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  background: ${({ theme }) => theme.background200};
  color: ${({ theme }) => theme.boldTextColor};
  border-radius: 16px;
  border-top-right-radius: 4px;
  font-size: 15px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

const AssistantText = styled.div`
  color: ${({ theme }) => theme.boldTextColor};
  font-size: 15px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
`;

const InputDock = styled.div`
  position: sticky;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    ${({ theme }) => theme.background} 30%
  );
  padding: 16px 24px 24px;
`;

const InputCard = styled.div`
  max-width: 760px;
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.background300};
  border-radius: 16px;
  background: ${({ theme }) => theme.background100};
  display: flex;
  align-items: flex-end;
  padding: 8px 8px 8px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
`;

const Input = styled.textarea`
  flex: 1;
  resize: none;
  border: none;
  outline: none;
  padding: 10px 0;
  font-size: 15px;
  line-height: 1.5;
  background: transparent;
  color: ${({ theme }) => theme.boldTextColor};
  font-family: inherit;
  max-height: 200px;
  &::placeholder {
    color: ${({ theme }) => theme.mainTextColor};
  }
`;

const SendButton = styled.button`
  background: ${({ theme }) => theme.accentColor};
  border: none;
  color: #000;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  &:disabled {
    background: ${({ theme }) => theme.background300};
    color: ${({ theme }) => theme.mainTextColor};
    cursor: not-allowed;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(FontAwesomeIcon)`
  animation: ${spin} 1s linear infinite;
  color: ${({ theme }) => theme.mainTextColor};
`;

const ThinkingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.mainTextColor};
  font-size: 14px;
`;

const ErrorBox = styled.div`
  max-width: 760px;
  margin: 0 auto 8px;
  padding: 10px 14px;
  background: #fdecea;
  color: #a12d22;
  border-radius: 8px;
  font-size: 13px;
`;

const ChatWidget = ({ onClose }: ChatWidgetProps) => {
  const { AuthState } = useAuthState() as any;
  const sessionToken: string | undefined = AuthState?.sessionToken;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [draft]);

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isSending) return;

    setError(null);
    setIsSending(true);

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed
    };
    setMessages((prev) => [...prev, userMsg]);
    setDraft('');

    try {
      const response = await sendAiMessage(
        sessionToken,
        trimmed,
        ASSISTANT_CHARACTER,
        conversationId
      );
      const data = response?.data ?? {};
      const responseMessages: any[] = Array.isArray(data.messages)
        ? data.messages
        : [];

      const assistantMsg = [...responseMessages]
        .reverse()
        .find((m) => m?.role === 'assistant');

      const nextConversationId =
        assistantMsg?.conversationId ??
        responseMessages[0]?.conversationId ??
        conversationId;

      if (nextConversationId && nextConversationId !== conversationId) {
        setConversationId(nextConversationId);
      }

      let assistantText = '';
      if (assistantMsg?.content) {
        try {
          const parsed = JSON.parse(assistantMsg.content);
          assistantText = parsed?.assistantText ?? assistantMsg.content;
        } catch {
          assistantText = assistantMsg.content;
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMsg?.objectId ?? `a-${Date.now()}`,
          role: 'assistant',
          content: assistantText || '(empty response)'
        }
      ]);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? e?.message ?? 'Failed to send message'
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Container>
      <TopBar>
        <BackButton onClick={onClose}>
          <FontAwesomeIcon icon={faArrowLeft as IconProp} />
          Back to dashboard
        </BackButton>
        <HeaderTitle>AI Assistant</HeaderTitle>
      </TopBar>

      <ScrollArea ref={scrollRef}>
        <MessagesColumn>
          {messages.length === 0 ? (
            <Empty>
              <EmptyTitle>Hoe kan ik helpen?</EmptyTitle>
              Stel een vraag over campaign- of leadperformance.
            </Empty>
          ) : (
            messages.map((m) =>
              m.role === 'user' ? (
                <MessageRow key={m.id} role="user">
                  <UserBubble>{m.content}</UserBubble>
                </MessageRow>
              ) : (
                <MessageRow key={m.id} role="assistant">
                  <AssistantText>{m.content}</AssistantText>
                </MessageRow>
              )
            )
          )}
          {isSending && (
            <MessageRow role="assistant">
              <ThinkingRow>
                <Spinner icon={faSpinner as IconProp} />
                Aan het nadenken...
              </ThinkingRow>
            </MessageRow>
          )}
        </MessagesColumn>
      </ScrollArea>

      <InputDock>
        {error && <ErrorBox>{error}</ErrorBox>}
        <InputCard>
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Vraag iets aan de AI Assistant..."
            rows={1}
            disabled={isSending}
          />
          <SendButton
            onClick={handleSend}
            disabled={isSending || !draft.trim()}
            aria-label="Send"
          >
            <FontAwesomeIcon icon={faPaperPlane as IconProp} />
          </SendButton>
        </InputCard>
      </InputDock>
    </Container>
  );
};

export default ChatWidget;
